import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import {
  useCreateSecretMutation,
  useLazyGetCredentialSecretSchemaQuery,
  useLazyGetSecretQuery,
  useUpdateSecretMutation,
} from "features/integrationsBeta/credentials";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";

import JsonSchemaForm from "./JsonSchemaForm";

type SecretObject = Record<string, unknown>;

interface Props {
  open: boolean;
  onClose: () => void;
  connectionId: number;
  mode: "add" | "edit";
  // The credential type is always fixed by the slot that opened the dialog -
  // secrets have adapter-declared schemas, never free-form types or fields.
  type: string;
  typeLabel: string;
  label?: string;
}

export default function SecretDialog({
  open,
  onClose,
  connectionId,
  mode,
  type,
  typeLabel,
  label,
}: Props) {
  const dispatch = useAppDispatch();
  const [createSecret, { isLoading: creating }] = useCreateSecretMutation();
  const [updateSecret, { isLoading: updating }] = useUpdateSecretMutation();
  const [fetchSecret] = useLazyGetSecretQuery();
  const [fetchSchema] = useLazyGetCredentialSecretSchemaQuery();

  const [labelDraft, setLabelDraft] = useState(label ?? "");
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null);
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  const [secretObj, setSecretObj] = useState<SecretObject>({});

  useEffect(() => {
    if (!open) return;
    setLabelDraft(label ?? "");
  }, [open, label]);

  useEffect(() => {
    if (!open) {
      setSchema(null);
      setSchemaLoaded(false);
      setSecretObj({});
      return;
    }
    fetchSchema({ connectionId, type })
      .unwrap()
      .then((r) => {
        setSchema((r.secret_schema as Record<string, unknown>) ?? null);
        setSchemaLoaded(true);
      })
      .catch(() => {
        setSchema(null);
        setSchemaLoaded(true);
      });

    if (mode === "edit") {
      fetchSecret({ connectionId, type })
        .unwrap()
        .then((detail) => setSecretObj((detail.secret ?? {}) as SecretObject))
        .catch(() => setSecretObj({}));
    } else {
      setSecretObj({});
    }
  }, [open, mode, connectionId, type, fetchSchema, fetchSecret]);

  const handleSave = async () => {
    if (!schema) return;
    try {
      if (mode === "add") {
        await createSecret({
          connectionId,
          type,
          secret: secretObj,
          label: labelDraft,
        }).unwrap();
      } else {
        await updateSecret({
          connectionId,
          type,
          secret: secretObj,
          label: labelDraft,
        }).unwrap();
      }
      dispatch(
        pushNotification({ severity: "success", message: "Credential saved." }),
      );
      onClose();
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not save the credential.",
        }),
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={mode === "add" ? `Configure ${typeLabel}` : `Edit ${typeLabel}`}
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={creating || updating || !schema}
          >
            Save
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField size="small" label="Type" value={typeLabel} disabled />

        <TextField
          size="small"
          label="Label"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
        />

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Secret fields
          </Typography>
          {schema ? (
            <JsonSchemaForm
              schema={schema}
              value={secretObj}
              onChange={setSecretObj}
            />
          ) : (
            schemaLoaded && (
              <Alert severity="warning">
                No secret schema is defined for this credential type. Ask a
                developer to add one to the provider adapter — free-form secrets
                are not allowed.
              </Alert>
            )
          )}
        </Box>
      </Stack>
    </AppDialog>
  );
}
